import React from 'react';

import {
  customsRequestLogApis,
  dashboardCustomsRequestLogApis,
} from '@apis/next/customsRequestLogApis';
import { Layout } from '@components/Layout';
import { PrintableContainer } from '@components/Printable';
import { QueryContainer } from '@components/QueryContainer';
import { cellFactory, Table } from '@components/Table/Table';
import { getChangeLogDefaultDetail } from '@data/changeLog';
import { customsRequestStrings } from '@data/customsRequest';
import { instructionStrings } from '@data/instruction';
import { useSearch } from '@hooks/next/useSearch';
import { type CustomsRequestLogFormatters } from '@models/next/CustomsRequestLog';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { formatDate, formatDateTime, toJST } from '@utils/types';
import clsx from 'clsx';
import { max } from 'lodash-es';
import { z } from 'zod';

const useStyles = makeMuiStyles((theme, _common) => ({
  customInner: {
    maxWidth: '65rem',
  },
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  dataNotExist: {
    color: theme.palette.grey[400],
    margin: '3rem auto',
  },
}));

const strings = createStrings({
  ja: {
    title: `${customsRequestStrings.changeLog} - ${customsRequestStrings.customsRequest}`,
    dataNotExist: '履歴なし',
    no: 'No',
    updatedAt: '更新時間',
    editor: '編集者',
    editedField: '変更内容',
    beforeValue: '変更前',
    afterValue: '変更後',
    detail: '変更理由',
  },
});

export interface PrintCustomsRequestLogProps {
  type: 'admin' | 'external';
}

export const PrintCustomsRequestLog: React.FC<PrintCustomsRequestLogProps> = ({ type }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    search: { d2d_no, customs_request },
  } = useSearch(
    z
      .object({
        d2d_no: z.string(),
        customs_request: z.string(),
      })
      .partial(),
  );

  const customsRequestLogQuery =
    type === 'admin'
      ? dashboardCustomsRequestLogApis.useGet('/dashboard/customs-request-log/:d2d_no', {
          params: {
            d2d_no: d2d_no!,
          },
        })
      : customsRequestLogApis.useGet('/customs-request-log', {
          queries: {
            customs_request: customs_request!,
          },
        });

  const formatters: CustomsRequestLogFormatters = {
    cargo_move_in_date: (v) => formatDate(v) ?? '-',
    free_time: (v) => formatDate(v) ?? '-',
    tentative_pickup_date: (v) => formatDate(v) ?? '-',
    'booking_inquiry.use_rcep': (v) => (v ? formStrings.use : formStrings.notUse),
    'pre_alert.vessel_schedule.eta': (v) => formatDate(v) ?? '-',
    'pre_alert.vessel_schedule.etd': (v) => formatDate(v) ?? '-',
    'customs_instruction.insurance.tag': (v) => instructionStrings.insuranceTags[v],
    'customs_instruction.insurance.start': (v) => formatDate(v) ?? '-',
    'customs_instruction.insurance.end': (v) => formatDate(v) ?? '-',
  };

  const bodyColumns =
    customsRequestLogQuery.data?.map((item, index) => {
      // Bug in typescript
      const formatter = (formatters[item.edited_field] ?? String) as typeof String;

      return [
        cellFactory.create((index + 1).toString()),
        cellFactory.create(item.editor || '-'),
        cellFactory.create(formatDateTime(item.updated_at)),
        cellFactory.create(item.label || '-'),
        cellFactory.create(formatter(item.before_value) || '-'),
        cellFactory.create(formatter(item.after_value) || '-'),
        cellFactory.create(item.detail || getChangeLogDefaultDetail(item) || '-'),
      ];
    }) ?? [];

  let contents: React.ReactNode = null;
  const head = (
    <Table.Head>
      <Table.Row
        columns={[
          cellFactory.create(strings.no || '-'),
          cellFactory.create(strings.editor || '-'),
          cellFactory.create(strings.updatedAt || '-'),
          cellFactory.create(strings.editedField || '-'),
          cellFactory.create(strings.beforeValue || '-'),
          cellFactory.create(strings.afterValue || '-'),
          cellFactory.create(strings.detail || '-'),
        ]}
      />
    </Table.Head>
  );

  if (customsRequestLogQuery.data && customsRequestLogQuery.data?.length > 0) {
    contents = (
      <Table>
        {head}
        <Table.Body>
          {bodyColumns.map((columns) => {
            return <Table.Row columns={columns} />;
          })}
        </Table.Body>
      </Table>
    );
  } else if (customsRequestLogQuery.data && customsRequestLogQuery.data?.length === 0) {
    contents = (
      <>
        <Table>{head}</Table>
        <p className={classes.dataNotExist}>{strings.dataNotExist}</p>
      </>
    );
  }

  const updatedAt =
    customsRequestLogQuery.data && max(customsRequestLogQuery.data.map((d) => d.updated_at));

  const pageContent = (
    <PrintableContainer classes={{ inner: classes.customInner }}>
      {updatedAt && (
        <p className={clsx(classes.horizontalSpace1, classes.middleText)}>
          <span>Latest Update:</span>
          <span>{updatedAt ? toJST(new Date(updatedAt)) : '-'}</span>
        </p>
      )}
      {contents}
    </PrintableContainer>
  );

  if (type === 'external') {
    return <QueryContainer queries={[customsRequestLogQuery]}>{pageContent}</QueryContainer>;
  }

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      loading={customsRequestLogQuery.isLoading}
      title={strings.title}
      errors={[customsRequestLogQuery.error]}
      classes={{
        background: classes.background,
      }}
    >
      {pageContent}
    </Layout>
  );
};
